import type { Session } from '@supabase/supabase-js';
import { currentSession, identityOf, supabase } from '../supabase';
import { isUserType, type UserType } from './roles';
import type { Answers } from './progress';

/**
 * What onboarding knows about a person. `answers` is the jsonb blob; everything
 * else is a real column, because it is either queried or assigned by the
 * database.
 */
export interface OnboardingProfile {
  userType: UserType | null;
  answers: Answers;
  /** Where they stopped, so a reload resumes rather than restarts. */
  stepIndex: number;
  completedAt: string | null;
  memberNo: number | null;
}

export const EMPTY_PROFILE: OnboardingProfile = {
  userType: null,
  answers: {},
  stepIndex: 0,
  completedAt: null,
  memberNo: null
};

interface OnboardingBlob {
  answers?: unknown;
  step?: unknown;
}

interface ProfileSelection {
  user_type: string | null;
  onboarding: OnboardingBlob | null;
  onboarding_completed_at: string | null;
  member_no: number | null;
}

const SELECT = 'user_type, onboarding, onboarding_completed_at, member_no';

/** jsonb comes back as `unknown`; keep only the string / string[] pairs. */
function readAnswers(raw: unknown): Answers {
  if (!raw || typeof raw !== 'object') return {};
  const answers: Answers = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string') answers[key] = value;
    else if (Array.isArray(value)) answers[key] = value.filter((item): item is string => typeof item === 'string');
  }
  return answers;
}

/**
 * Reads the onboarding state for the signed-in person.
 *
 * A read that fails — most likely the migration in
 * `supabase/migrations/20260906120000_onboarding_profiles.sql` has not been
 * applied — hands back the empty state rather than an error page. Onboarding is
 * the thing that repairs an empty profile, so sending someone there is the right
 * answer either way.
 */
export async function loadOnboarding(session: Session): Promise<OnboardingProfile> {
  const { data, error } = await supabase()
    .from('profiles')
    .select(SELECT)
    .eq('id', session.user.id)
    .maybeSingle<ProfileSelection>();

  if (error || !data) {
    if (error) console.error('Onboarding read failed:', error.message);
    return EMPTY_PROFILE;
  }

  const blob = data.onboarding || {};
  const step = typeof blob.step === 'number' && Number.isFinite(blob.step) ? Math.max(0, Math.trunc(blob.step)) : 0;

  return {
    userType: isUserType(data.user_type) ? data.user_type : null,
    answers: readAnswers(blob.answers),
    stepIndex: step,
    completedAt: data.onboarding_completed_at,
    memberNo: typeof data.member_no === 'number' ? data.member_no : null
  };
}


/**
 * Turns a PostgREST failure into something a person can act on.
 *
 * The raw text is accurate and useless to whoever is mid-onboarding: "new row
 * violates row-level security policy for table profiles" reads as though they
 * did something wrong, when it means a policy was never applied. The original
 * still goes to the console for whoever is actually going to fix it.
 */
function readableError(error: { code?: string; message: string }): Error {
  console.error('Onboarding write failed:', error.code, error.message);

  const message = error.message.toLowerCase();

  if (error.code === '42501' || message.includes('row-level security')) {
    return new Error(
      'Your answers could not be saved — this account is not permitted to write its own profile. That is a setup problem on our side, not anything you did.'
    );
  }
  if (error.code === '42703' || message.includes('schema cache') || message.includes('does not exist')) {
    return new Error('Your answers could not be saved — the profile store is not ready yet. Nothing you entered was wrong.');
  }
  if (error.code === '23505') {
    return new Error('That profile already exists under a different account. Sign in with the account you used before.');
  }
  return new Error('Could not save your answers. Check your connection and try again.');
}

interface SavePatch {
  userType?: UserType;
  answers?: Answers;
  stepIndex?: number;
  complete?: boolean;
}

/**
 * Writes the state back, upserting so the row exists even if the sign-in sync
 * never ran. Failures throw: unlike the profile mirror at sign-in, losing these
 * answers means the person retypes them.
 */
export async function saveOnboarding(current: OnboardingProfile, patch: SavePatch): Promise<OnboardingProfile> {
  const session = await currentSession();
  if (!session) throw new Error('Your session expired. Sign in again to keep your answers.');

  const next: OnboardingProfile = {
    ...current,
    userType: patch.userType ?? current.userType,
    answers: patch.answers ?? current.answers,
    stepIndex: patch.stepIndex ?? current.stepIndex,
    completedAt: patch.complete ? current.completedAt || new Date().toISOString() : current.completedAt
  };

  const who = identityOf(session);
  const row: Record<string, unknown> = {
    id: who.id,
    email: who.email,
    full_name: (next.answers.full_name as string) || who.name,
    avatar_url: who.avatarUrl,
    user_type: next.userType,
    onboarding: { answers: next.answers, step: next.stepIndex }
  };
  if (next.completedAt) row.onboarding_completed_at = next.completedAt;

  const { data, error } = await supabase()
    .from('profiles')
    .upsert(row, { onConflict: 'id' })
    .select(SELECT)
    .maybeSingle<ProfileSelection>();

  if (error) throw readableError(error);

  // The membership number is assigned by the database on insert, so it only
  // becomes knowable after a write.
  return { ...next, memberNo: typeof data?.member_no === 'number' ? data.member_no : next.memberNo };
}

export function onboardingComplete(profile: OnboardingProfile): boolean {
  return Boolean(profile.userType && profile.completedAt);
}
